import React, { useState, useEffect, useRef, createRef, memo, useMemo } from "react";
import {
  Constants,
  useMeeting,
  useParticipant,
  usePubSub,
  useTranscription,
  useTranslation,
} from "@videosdk.live/react-sdk";
import { SidebarConatiner } from "../components/sidebar/SidebarContainer";
import MemorizedParticipantView from "./components/ParticipantView";
import FocusParticipantView from "./components/FocusParticipantView";
import { PresenterView } from "../components/PresenterView";
import WaitingToJoinScreenNew from "../components/screens/WaitingToJoinScreenNew";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import useWindowSize from "../hooks/useWindowSize";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import TopBar from "./components/TopBar";
import { sideBarModes } from "../utils/common";
import { useSiteContext } from "../context/SiteContext";
import WaitingForApproval from "../components/screens/WaitingForApproval";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";
import { useWhiteboard } from "@videosdk.live/react-sdk";
import ElementSection from "../components/meeting/ElementSection";
import CtaSection from "../components/meeting/CtaSection";
import DocumentViewer from "../components/meeting/DocumentViewer";
import PrompterSection from "../components/meeting/PrompterSection";
import { getFolderElements } from "../services/elementServices";
import VideoSection from "../components/meeting/VideoSection";
import WebsiteSection from "../components/meeting/WebsiteSection";
import SpeakerParticipantView from "./components/SpeakerParticipantView";
import SpeakerSideParticipantView from "./components/SpeakerSideParticipantView";
import PollVotingModal from "../components/meeting/PollVotingModal";
import PollResults from "./components/PollResults";
import { initGetStreamChat } from "../utils/getStreamClient";
import test_sound from "../sounds/test_sound.wav";
import { ParticipantPermissionModal } from "../components/modals/PermissionRequestModal";
import WebViewer from "../components/meeting/WebViewer";

export function MeetingContainerMobile({
  onMeetingLeave,
  setIsMeetingLeft,
  setCustomVideoStream,
  micOn,
  setMicOn,
  webcamOn,
  setWebcamOn,
  customAudioStream,
  setCustomAudioStream,
  customVideoStream,
}) {
  const [showJoinScreen, setShowJoinScreen] = useState(null);
  const [showWaitingApproval, setShowWaitingApproval] = useState(false);
  const [participantsData, setParticipantsData] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [showPollModal, setShowPollModal] = useState(false);

  // Permission modal states
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [showVideoPermissionModal, setShowVideoPermissionModal] =
    useState(false);
  const {
    meetingSettings,
    showElementSection,
    setFolderElementList,
    setShowElementSection,
    activeElement,
    setActiveElement,
    meetingRole,
    getMeetingSession,
    meetingSession,
    selectedLayout,
    translationStatus,
    setTranslationStatus,
    setIsMeetingReconnecting,
    isMeetinngReconnecting,
    showPollResults,
    setShowPollResults,
    setMeetingSettings,
    prompterElement,
    setPrompterElement,
  } = useSiteContext();
  const { stopWhiteboard, whiteboardUrl } = useWhiteboard();
  const approvalSoundRef = useRef(null);
  const [isElementRefreshing, setIsElementRefreshing] = useState(false);
  // const ParticipantMicStream = memo(({ participantId }) => {
  //   // Individual hook for each participant
  //   const { micStream } = useParticipant(participantId);

  //   useEffect(() => {

  //     if (micStream) {
  //       const mediaStream = new MediaStream();
  //       mediaStream.addTrack(micStream.track);

  //       const audioElement = new Audio();
  //       audioElement.srcObject = mediaStream;
  //       audioElement.play();

  //     }
  //   }, [micStream, participantId]);

  //   return null;
  // }, [participantsData]);

  // const ParticipantMicStream = memo(
  //     ({ participantId }) => {
  //         // Individual hook for each participant
  //         const { micStream, isLocal } = useParticipant(participantId);

  //         useEffect(() => {
  //             if (micStream) {
  //                 const mediaStream = new MediaStream();
  //                 mediaStream.addTrack(micStream.track);

  //                 const audioElement = new Audio();
  //                 audioElement.srcObject = mediaStream;
  //                 audioElement.muted = isLocal;
  //                 audioElement.play();
  //             }
  //         }, [micStream, participantId]);

  //         return null;
  //     },
  //     [participantsData]
  // );

  const ParticipantMicStream = memo(({ participantId }) => {
    const { micStream, isLocal } = useParticipant(participantId);
    const audioRef = useRef(null);

    useEffect(() => {
      if (!micStream) return;

      // Create audio element only once
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.autoplay = true;
      }

      const mediaStream = new MediaStream();
      mediaStream.addTrack(micStream.track);

      audioRef.current.srcObject = mediaStream;
      audioRef.current.muted = isLocal;

      audioRef.current.play().catch(() => { });

      return () => {
        // Cleanup when micStream or participant changes
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.srcObject = null;
        }
      };
    }, [micStream, isLocal]);

    return null;
  });

  const { isInSameRoom, currentRoom, participantRooms, rooms } = useBreakoutRooms();

  const {
    useRaisedHandParticipants,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    setUnreadChat,
    sideBarMode,
    setRecentReactions,
    transcriptionEnabled,
    participantApproveFunc,
    setParticipantApproveFunc,
    approvalSoundEnabled,
    translationEnabled,
    setTranslationEnabled,
    setActiveSpeakerId,
    setShowRecordingConsentModal,
    selectedLanguage,
  } = useMeetingAppContext();
  const bottomBarHeight = 60;

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] =
    useState(null);
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);
  const [transcriptions, setTranscriptions] = useState(null);
  const [isTranscriptionPanelVisible, setIsTranscriptionPanelVisible] =
    useState(false);
  const PANEL_FIXED_HEIGHT = 180;
  const PANEL_MOBILE_HEIGHT = 120; // Reduced height for mobile
  const [panelSize, setPanelSize] = useState({
    width: 450,
    height: PANEL_FIXED_HEIGHT,
  });
  const [panelPosition, setPanelPosition] = useState(() => {
    // Default to bottom-centered on desktop
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const panelHeight = PANEL_FIXED_HEIGHT;
    const initialWidth = 450; // matches initial panelSize.width
    return {
      x: Math.max(0, Math.floor((windowWidth - initialWidth) / 2)),
      y: windowHeight - panelHeight - 20,
    };
  });

  const [mobilePanelPosition, setMobilePanelPosition] = useState(() => {
    const windowWidth = window.innerWidth;
    const initialWidth = Math.min(450, windowWidth - 32);
    return {
      x: (windowWidth - initialWidth) / 2,
      y: 20,
    };
  });

  // Panel size constraints
  const PANEL_MIN_WIDTH = 300;
  const PANEL_MAX_WIDTH = window.innerWidth; // Allow expansion up to full browser width
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchDragOffset, setTouchDragOffset] = useState({ x: 0, y: 0 });
  const transcriptionPanelRef = useRef(null);
  const transcriptionListRef = useRef(null);

  // Whiteboard dragging and resizing states
  const [whiteboardPosition, setWhiteboardPosition] = useState(() => {
    const windowHeight = window.innerHeight;
    const whiteboardHeight = 600;
    return {
      x: 20, // 20px from left
      y: (windowHeight - whiteboardHeight) / 2,
    };
  });
  const [whiteboardSize, setWhiteboardSize] = useState({
    width: 800,
    height: 600,
  });
  const [isWhiteboardDragging, setIsWhiteboardDragging] = useState(false);
  const [isWhiteboardResizing, setIsWhiteboardResizing] = useState(false);
  const [whiteboardDragOffset, setWhiteboardDragOffset] = useState({
    x: 0,
    y: 0,
  });
  const [whiteboardResizeHandle, setWhiteboardResizeHandle] = useState(null);
  const whiteboardRef = useRef(null);

  const mMeetingRef = useRef();
  const containerRef = createRef();
  const containerHeightRef = useRef();
  const containerWidthRef = useRef();

  useEffect(() => {
    containerHeightRef.current = containerHeight;
    containerWidthRef.current = containerWidth;
  }, [containerHeight, containerWidth]);

  useEffect(() => {
    if (showJoinScreen) {
      getMeetingSession(meetingSettings.roomId);
    }
  }, [showJoinScreen]);

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const windowSize = useWindowSize();
  const isMobileLike =
    (windowSize?.height || 0) > 0 && windowSize.height < 500 ? true : isMobile;
  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobileLike && isLandscapeOrientation;
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [mobilePanelWidth, setMobilePanelWidth] = useState(0);
  const [isBottomParticipantCollapsed, setIsBottomParticipantCollapsed] =
    useState(false);

  const sideBarContainerWidth = isXLDesktop
    ? 400
    : isLGDesktop
      ? 360
      : isTab
        ? 320
        : isMobile
          ? 280
          : 240;

  useEffect(() => {
    if (isMobile) {
      const widthForMobile = Math.min(window.innerWidth - 32, panelSize.width);
      setPanelSize((prev) => ({
        ...prev,
        width: widthForMobile,
        height: PANEL_MOBILE_HEIGHT,
      }));
      setPanelPosition({
        x: (window.innerWidth - widthForMobile) / 2,
        y: window.innerHeight - PANEL_MOBILE_HEIGHT - 20,
      });
    }
  }, [isMobile]);

  // Reserve space when the mobile panel is open in landscape
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      setIsMobilePanelOpen(!!detail.open);
      setMobilePanelWidth(detail.width || 0);
    };
    window.addEventListener("rlink:mobile-panel", handler);
    return () => window.removeEventListener("rlink:mobile-panel", handler);
  }, []);

  useEffect(() => {
    containerRef.current?.offsetHeight &&
      setContainerHeight(containerRef.current.offsetHeight);
    containerRef.current?.offsetWidth &&
      setContainerWidth(containerRef.current.offsetWidth);

    window.addEventListener("resize", ({ target }) => {
      containerRef.current?.offsetHeight &&
        setContainerHeight(containerRef.current.offsetHeight);
      containerRef.current?.offsetWidth &&
        setContainerWidth(containerRef.current.offsetWidth);
    });
  }, [containerRef]);

  const { participantRaisedHand } = useRaisedHandParticipants();

  const _handleMeetingLeft = () => {
    setIsMeetingLeft(true);
  };

  const _handleOnRecordingStateChanged = ({ status }) => {
    console.log("onRecordingStateChanged", status);
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      if (status === Constants.recordingEvents.RECORDING_STARTED) {
        if (!["moderator", "co-host"].includes(meetingRole)) {
          setShowRecordingConsentModal(true);
        }
      }
      // toast(
      //   `${status === Constants.recordingEvents.RECORDING_STARTED
      //     ? "Meeting recording is started"
      //     : "Meeting recording is stopped."
      //   }`,
      //   {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   }
      // );
    }
  };

  function onParticipantJoined(participant) {
    console.log("onParticipantJoined", participant, mMeeting);
    // Change quality to low, med or high based on resolution
    participant && participant.setQuality("low");
  }

  function onEntryRequested(data) {
    const { participantId, name, allow, deny } = data;
    console.log("onEntryRequested", data);
    // play audio from assets
    // read latest meeting settings from state

    let hostApprovalRequired = meetingSettings.hostApprovalRequired;
    setMeetingSettings((prev) => {
      hostApprovalRequired = prev.hostApprovalRequired;
      return prev;
    });
    if (hostApprovalRequired) {
      const shouldPlaySound =
        ["moderator", "co-host"].includes(meetingRole) &&
        approvalSoundEnabled === true;

      if (shouldPlaySound && approvalSoundRef?.current) {
        approvalSoundRef.current.play().catch((err) => {
          console.log("Error playing approval sound:", err);
        });
      }
      setParticipantApproveFunc((prevFuncs) => [
        ...prevFuncs,
        { name, participantId, allow, deny },
      ]);
      setTimeout(() => {
        console.log("participantApproveFunc", participantApproveFunc);
      }, 10000);
      publishMeetingSettings(JSON.stringify({
        data: meetingSettings,
      }), { persist: true, sendOnly: participantId });
    } else {
      allow();
      publishMeetingSettings(JSON.stringify({
        data: meetingSettings,
      }), { persist: true, sendOnly: participantId });
    }
  }

  function onEntryResponded(participantId, name) {
    console.log("onEntryResponded", participantId, name);
    if (mMeetingRef.current?.localParticipant?.id === participantId) {
      if (name === "allowed") {
        setLocalParticipantAllowedJoin(true);
      } else {
        setLocalParticipantAllowedJoin(false);
        console.log("onEntryResponded", name);
        setTimeout(() => {
          _handleMeetingLeft();
        }, 3000);
      }
    }
  }

  function onMeetingJoined() {

  }

  function onMeetingLeft() {
    setSelectedMic({ id: null, label: null });
    setSelectedWebcam({ id: null, label: null });
    setSelectedSpeaker({ id: null, label: null });
    onMeetingLeave();
    setIsMeetingReconnecting(false);
  }

  const _handleOnError = (data) => {
    const { code, message } = data;
    console.log("meetingErr", code, message);

    const joiningErrCodes = [
      4001, 4002, 4003, 4004, 4005, 4006, 4007, 4008, 4009, 4010,
    ];

    const isJoiningError = joiningErrCodes.findIndex((c) => c === code) !== -1;
    const isCriticalError = `${code}`.startsWith("500");

    // new Audio(
    //     isCriticalError
    //         ? `https://static.videosdk.live/prebuilt/notification_critical_err.mp3`
    //         : `https://static.videosdk.live/prebuilt/notification_err.mp3`
    // ).play();

    setMeetingErrorVisible(true);
    setMeetingError({
      code,
      message: isJoiningError ? "Unable to join meeting!" : message,
    });
  };

  const { enableAdaptiveSubscriptions, ...mMeeting } = useMeeting({
    onParticipantJoined,
    onMeetingJoined,
    onMeetingStateChanged: ({ state }) => {
      // toast(`Meeting is in ${state} state`, {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      // });
      console.log("onMeetingStateChanged", state);
      if (state === "CONNECTING") {
        setShowWaitingApproval(true);
      }

      if (state === "RECONNECTING") {
        console.log("Meeting is Reconnecting");
        setIsMeetingReconnecting(true);
      }

      if (state === "CONNECTED") {
        setShowWaitingApproval(false);
        console.log("isMeetinngReconnecting", isMeetinngReconnecting);
        let isReconnecting = isMeetinngReconnecting;
        setIsMeetingReconnecting((prev) => {
          console.log("prev", prev);
          isReconnecting = prev;
          return prev;
        });
        setIsMeetingReconnecting(false);
        if (!isReconnecting) {
          setShowJoinScreen(true);
          console.log("meetingSettings.roomId", meetingSettings.roomId);
          setTimeout(() => {
            setShowJoinScreen(false);
          }, 3500);
        }
      }

      if (state === "DISCONNECTED") {
        setIsMeetingReconnecting(false);
      }
    },
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
    onEntryRequested: onEntryRequested,
    onEntryResponded: onEntryResponded,
    onSpeakerChanged: (newActiveSpeakerId) => {
      if (newActiveSpeakerId) {
        console.log("Active Speaker participantId", newActiveSpeakerId);
        setActiveSpeakerId(newActiveSpeakerId);
      }
    },
  });

  // Filter screen share by breakout room - only show screen share from participants in the same room
  const presenterId = mMeeting.presenterId;
  const isPresenting = presenterId ? true : false;

  // Check if presenter is in the same room (for breakout room isolation)
  const shouldShowScreenShare = React.useMemo(() => {
    if (!presenterId) return false;
    if (presenterId === mMeeting?.localParticipant?.id) return true; // Always show own screen share

    if (!currentRoom) {
      // If in main meeting, only show screen shares from participants NOT in any breakout room
      return !participantRooms[presenterId];
    }

    // If in a breakout room, only show screen share if presenter is in the same room
    return isInSameRoom(presenterId);
  }, [presenterId, mMeeting?.localParticipant?.id, currentRoom, isInSameRoom, participantRooms]);

  const effectiveIsPresenting = isPresenting && shouldShowScreenShare;

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const participantIds = Array.from(mMeeting.participants.keys());
      console.log("Debounced participantIds", mMeeting.participants);
      console.log("mMeeting.participants", mMeeting.participants);
      setParticipantsData(participantIds);
      // get from object if any participant is moderator
      const hasModerator = participantIds.find(
        (participant) =>
          mMeeting.participants[participant] &&
          mMeeting.participants[participant].value &&
          ["moderator", "co-host"].includes(
            mMeeting.participants[participant].value.metaData.meetingRole
          )
      );
      console.log("hasModerator", hasModerator);
      console.log("Setting participants");
      // if (isPresenting && videoResolution == 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('low');
      // } else if (participantIds.length > 10 && videoResolution == 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('high');
      // } else if (participantIds.length <= 10 && videoResolution != 'high' && mMeeting.localParticipant.webcamOn) {
      //   handleVideoResolution('low');
      // }
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [mMeeting.participants]);

  useEffect(() => {
    // console.log("meetingSession", meetingSession);
    // console.log("mMeeting", mMeeting);
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName } = data;

      const isLocal = senderId === localParticipantId;

      // Call the toggle function and get the action
      const action = participantRaisedHand(senderId);
      const emoji = action === "lowered" ? "✋" : "🖐🏼";

      // new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      // ).play();

      // toast(
      //   `${
      //     isLocal ? "You" : nameTructed(senderName, 15)
      //   } ${action} hand ${emoji}`,
      //   {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   }
      // );
    },
  });

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName, message } = data;

      const isLocal = senderId === localParticipantId;

      // Filter chat messages by breakout room - only show messages from participants in the same room
      if (!isLocal) {
        if (currentRoom) {
          // If in a breakout room, only process messages from participants in the same room
          if (!isInSameRoom(senderId)) {
            console.log("🔴 Chat message filtered out - sender not in same breakout room:", senderId);
            return; // Ignore messages from participants in different rooms
          }
        } else {
          // If in main meeting, only process messages from participants NOT in any breakout room
          if (participantRooms[senderId]) {
            console.log("🔴 Chat message filtered out - sender is in a breakout room:", senderId);
            return; // Ignore messages from participants in breakout rooms
          }
        }
      }

      if (!isLocal) {
        console.log("SIDE BAR MODE CHAT", sideBarMode, sideBarModes.CHAT);
        if (sideBarMode != sideBarModes.CHAT) {
          setUnreadChat(true);
        } else {
          setUnreadChat(false);
        }

        // new Audio(
        //     `https://static.videosdk.live/prebuilt/notification.mp3`
        // ).play();

        // toast(
        //     `${trimSnackBarText(
        //         `${nameTructed(senderName, 15)} says: ${message}`
        //     )}`,
        //     {
        //         position: "bottom-left",
        //         autoClose: 4000,
        //         hideProgressBar: true,
        //         closeButton: false,
        //         pauseOnHover: true,
        //         draggable: true,
        //         progress: undefined,
        //         theme: "light",
        //     }
        // );
      }
    },
  });

  const { publish: publishMeetingSettings } = usePubSub("MEETING_SETTINGS", {
    onMessageReceived: (data) => {
      console.log("MEETING_SETTINGS", data);

      const message = typeof data.message === "string" ? JSON.parse(data.message) : data.message;

      if (!message.data) return;
      setMeetingSettings(message.data);
    },
  });

  useEffect(() => {
    let isCancelled = false;
    let unsubscribe = null;

    const setupStreamChatListener = async () => {
      try {
        const userId = mMeeting?.localParticipant?.id;
        const userName = mMeeting?.localParticipant?.displayName || "Guest";
        const baseMeetingId = meetingSession || mMeeting?.meetingId;
        if (!userId || !baseMeetingId) return;

        const client = await initGetStreamChat(userId, userName);
        if (!client || isCancelled) return;

        // Determine channel ID based on breakout room status
        let channelId = baseMeetingId;
        if (currentRoom && rooms[currentRoom]) {
          // If in a breakout room, use channel ID with room name and session ID
          const roomName = rooms[currentRoom].name || currentRoom;
          const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
          channelId = `${baseMeetingId}-${sanitizedRoomName}-${currentRoom}`;
        }

        const channel = client.channel("livestream", channelId);
        try {
          await channel.watch();
        } catch (e) { }
        // try {
        //   await channel.addMembers([userId]);
        // } catch (e) { }

        unsubscribe = channel.on("message.new", (event) => {
          try {
            const msg = event?.message;
            if (!msg) return;

            if (msg.user?.id === userId) return;

            // Filter chat messages by breakout room - only show messages from participants in the same room
            const senderId = msg.user?.id;
            if (currentRoom && senderId) {
              // If in a breakout room, only process messages from participants in the same room
              if (!isInSameRoom(senderId)) {
                console.log("🔴 Stream Chat message filtered out - sender not in same breakout room:", senderId);
                return; // Ignore messages from participants in different rooms
              }
            } else if (!currentRoom && senderId) {
              // If in main meeting, only process messages from participants not in any room
              if (participantRooms[senderId]) {
                console.log("🔴 Stream Chat message filtered out - sender is in a breakout room:", senderId);
                return; // Ignore messages from participants in breakout rooms
              }
            }

            if (sideBarMode !== sideBarModes.CHAT) {
              setUnreadChat(true);
            }
          } catch (e) { }
        });
      } catch (e) { }
    };

    setupStreamChatListener();

    return () => {
      isCancelled = true;
      try {
        unsubscribe && unsubscribe();
      } catch (e) { }
    };
  }, [
    mMeeting?.localParticipant?.id,
    mMeeting?.meetingId,
    meetingSession,
    sideBarMode,
    setUnreadChat,
    currentRoom,
    isInSameRoom,
    participantRooms,
    rooms,
  ]);

  usePubSub("MUTE_MIC", {
    onMessageReceived: (data) => {
      console.log("MUTE_MIC", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { participantId } = messageData;
      const isMatch = participantId === localParticipantId;
      if (isMatch) {
        mMeeting.toggleMic();
      }
    },
  });

  // Listen for microphone permission requests
  usePubSub("MIC_PERMISSION_REQUEST", {
    onMessageReceived: (data) => {
      console.log("MIC_PERMISSION_REQUEST", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { targetParticipantId, granted } = messageData;

      if (targetParticipantId === localParticipantId && granted) {
        setShowMicPermissionModal(true);
      }
    },
  });

  // Listen for video permission requests
  usePubSub("VIDEO_PERMISSION_REQUEST", {
    onMessageReceived: (data) => {
      console.log("VIDEO_PERMISSION_REQUEST", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { targetParticipantId, granted } = messageData;

      if (targetParticipantId === localParticipantId && granted) {
        setShowVideoPermissionModal(true);
      }
    },
  });

  // Handlers for accepting/declining microphone permission
  const handleAcceptMicPermission = () => {
    console.log("handleAcceptMicPermission", mMeeting.localMicOn);
    if (mMeeting) {
      mMeeting.unmuteMic();
    }
    setShowMicPermissionModal(false);
  };

  const handleDeclineMicPermission = () => {
    setShowMicPermissionModal(false);
  };

  // Handlers for accepting/declining video permission
  const handleAcceptVideoPermission = () => {
    if (mMeeting && !mMeeting.localWebcamOn) {
      mMeeting.enableWebcam();
      setWebcamOn(true);
    }
    setShowVideoPermissionModal(false);
  };

  const handleDeclineVideoPermission = () => {
    setShowVideoPermissionModal(false);
  };

  usePubSub("SHOW_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      console.log("SHOW_ELEMENT_SECTION", data);
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      setActiveElement(messageData);
    },
  });
  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );

  usePubSub("CLOSE_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      setActiveElement(null);
    },
  });

  // Main meeting reactions - only process if NOT in a breakout room
  usePubSub("EMOJI_REACTION", {
    onMessageReceived: (data) => {
      console.log("EMOJI_REACTION", data);

      // Only process main meeting reactions if we're in the main meeting
      if (currentRoom) {
        console.log("🔴 Main meeting reaction ignored - user is in breakout room:", currentRoom);
        return;
      }

      const localParticipantId = mMeeting?.localParticipant?.id;
      const senderId = data.senderId;

      // Ignore reactions from participants in breakout rooms
      if (senderId !== localParticipantId && participantRooms[senderId]) {
        console.log("🔴 Reaction filtered out - sender is in a breakout room:", senderId);
        return;
      }

      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { char, alt } = messageData;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const reaction = {
        id,
        from: data.senderName,
        senderId: senderId,
        emoji: char,
        emojiType: alt,
        ts: Date.now(),
      };
      setRecentReactions((prev) => [...prev.slice(-5), reaction]);
      setTimeout(() => {
        setRecentReactions((prev) => prev.filter((r) => r.id !== id));
      }, 2500);
    },
  });

  // Breakout room reactions - only process if in a breakout room
  usePubSub("EMOJI_REACTION_BREAKOUT", {
    onMessageReceived: (data) => {
      console.log("EMOJI_REACTION_BREAKOUT", data);

      // Only process breakout reactions if we're in a breakout room
      if (!currentRoom) {
        console.log("🔴 Breakout reaction ignored - user is in main meeting");
        return;
      }

      const localParticipantId = mMeeting?.localParticipant?.id;
      const senderId = data.senderId;

      // Only process reactions from participants in the same breakout room
      if (senderId !== localParticipantId && !isInSameRoom(senderId)) {
        console.log("🔴 Breakout reaction filtered out - sender not in same room:", senderId);
        return;
      }

      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { char, alt } = messageData;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const reaction = {
        id,
        from: data.senderName,
        senderId: senderId,
        emoji: char,
        emojiType: alt,
        ts: Date.now(),
      };
      setRecentReactions((prev) => [...prev.slice(-5), reaction]);
      setTimeout(() => {
        setRecentReactions((prev) => prev.filter((r) => r.id !== id));
      }, 2500);
    },
  });

  // Clear reactions when switching between main meeting and breakout rooms
  useEffect(() => {
    setRecentReactions([]);
  }, [currentRoom, setRecentReactions]);

  usePubSub("POLL", {
    onMessageReceived: (data) => {
      console.log("POLL received", data);
      // Handle both data.message and direct data structure
      const rawMessage = data.message || data;
      const pollData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { type, poll } = pollData;

      if (
        type === "launch" &&
        poll &&
        data.senderId != mMeeting?.localParticipant?.id
      ) {
        console.log("Launching poll:", poll);
        setActivePoll(poll);
        setShowPollModal(true);
      }
    },
  });

  function onTranslationStateChanged(data) {
    const { status, id } = data;
    console.log("onTranslationStateChanged", data);
    if (status === Constants.translationEvents.TRANSLATION_STARTING) {
      setTranslationStatus("starting");
    } else if (status === Constants.translationEvents.TRANSLATION_STARTED) {
      setTranslationStatus("started");
    } else if (status === Constants.translationEvents.TRANSLATION_STOPPING) {
      setTranslationStatus("stopping");
    } else if (status === Constants.translationEvents.TRANSLATION_STOPPED) {
      setTranslationStatus("stopped");
    }
  }

  function onTranslationText(data) {
    console.log("onTranslationText", data);
    let transenabled = translationEnabled;
    setTranslationEnabled((prev) => {
      console.log("prev", prev);
      transenabled = prev;
      return prev;
    });
    if (!transenabled) return;

    let { participantId, participantName, text, timestamp, type } = data;

    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    const profileImage =
      participant?.metaData?.profileImage ||
      participant?.value?.metaData?.profileImage ||
      null;
    setTranscriptions({
      participantId,
      participantName,
      text: text.trim(),
      timestamp,
      type,
      profileImage,
    });
  }

  function onTranslationTextOld(data) {
    console.log("onTranslationText", data);
    let { participantId, participantName, text, timestamp, type } = data;

    if (!text) return;
    const cleanedText = text.trim();

    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    const profileImage =
      participant?.metaData?.profileImage ||
      participant?.value?.metaData?.profileImage ||
      null;
    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      // Handle partial and full types
      if (type === "partial") {
        // For partial: if last entry from same participant exists, update it (always same index)
        if (lastTranscription) {
          // Replace existing entry with new partial (progressively updating same line)
          return prev.map((t, index) =>
            index === lastIndex
              ? {
                ...t,
                text: cleanedText,
                timestamp,
                type: "partial",
                profileImage: profileImage || t.profileImage,
              }
              : t
          );
        }

        // No existing entry from this participant, create new partial entry
        const transcriptionEntry = {
          id: `${participantId}-${timestamp}`,
          participantId,
          participantName,
          text: cleanedText,
          timestamp,
          type,
          profileImage,
        };
        return [...prev, transcriptionEntry];
      }

      if (type === "full") {
        // For full: replace partial at same index if exists
        if (lastTranscription && lastTranscription.type === "partial") {
          // Replace partial with full at same index
          return prev.map((t, index) =>
            index === lastIndex
              ? {
                ...t,
                text: cleanedText,
                timestamp,
                type: "full",
                profileImage: profileImage || t.profileImage,
              }
              : t
          );
        }

        // Previous full or no previous entry: create new entry (new line)
        const transcriptionEntry = {
          id: `${participantId}-${timestamp}`,
          participantId,
          participantName,
          text: cleanedText,
          timestamp,
          type,
          profileImage,
        };
        return [...prev, transcriptionEntry];
      }

      // No type specified - just add as new entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };
      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  }
  function onTranslationLanguageChanged(data) {
    const { participantId, language } = data;
    console.log("onTranslationLanguageChanged", data);
    console.log(
      `Participant ${participantId} changed translation language to ${language}`
    );
  }

  const { startTranslation, stopTranslation, changeTranslationLanguage } =
    useTranslation({
      onTranslationStateChanged,
      onTranslationText,
      onTranslationLanguageChanged,
    });

  // PubSub for translation control - DO NOT automatically start translation on other participants
  // Each participant controls their own translation independently
  const { publish: publishTranslationControl } = usePubSub("TRANSLATION_CONTROL", {
    onMessageReceived: (data) => {
      // Disabled: Don't automatically start/stop translation on other participants
      // Each participant should control their own translation independently
      console.log("TRANSLATION_CONTROL received (ignored - no auto-sync)", data);
      return;
    },
  });

  // Wrapper function to start translation (only for local participant)
  const handleStartTranslation = () => {
    // Only start if not already enabled
    // if (!translationEnabled && translationStatus === "stopped") {
    // Start translation only for this participant (no PubSub sync)
    if (mMeeting && mMeeting.translationState == Constants.translationEvents.TRANSLATION_STOPPED) {
      startTranslation();
    }
    setTranslationEnabled(true);
  };

  // Wrapper function to stop translation (only for local participant)
  const handleStopTranslation = () => {
    // Only stop if currently enabled
    if (translationEnabled) {
      // Stop translation only for this participant (no PubSub sync)
      // stopTranslation();
      setTranslationEnabled(false);
    }
  };

  // transcription
  // Use REACT_APP_WEBHOOK_URL if set, otherwise use Cloudflare tunnel URL (required for webhooks to work)
  const webhookUrl = process.env.REACT_APP_TRANSCRIPTION_WEBHOOK_URL;

  const config = {
    webhookUrl: webhookUrl,
    summary: {
      enabled: true,
      prompt:
        "Write summary in sections like Title, Agenda, Speakers, Action Items, Outlines, Notes and Summary",
    },
  };

  function onTranscriptionStateChanged(data) {
    const { status, id } = data;
    console.log("Transcription state changed:", status, id);

    if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTING) {
      console.log("Realtime Transcription is starting", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTED) {
      console.log("Realtime Transcription is started", id);
    } else if (
      status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
    ) {
      console.log("Realtime Transcription is stopping", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPED) {
      console.log("Realtime Transcription is stopped", id);
    }
  }

  // Callback function for transcription text event with smart deduplication
  const onTranscriptionText = (data) => {
    // how to read hooks here?
    let transenabled = translationEnabled;
    setTranslationEnabled((prev) => {
      console.log("prev", prev);
      transenabled = prev;
      return prev;
    });
    console.log("transenabled", transenabled);
    if (transenabled) return;
    let { participantId, participantName, text, timestamp, type } = data;
    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    setTranscriptions({
      participantId,
      participantName,
      text,
      timestamp,
      type,
      profileImage:
        participant?.metaData?.profileImage ||
        participant?.value?.metaData?.profileImage ||
        null,
    });
    return;
    console.log("onTranscriptionText", data);
    // Filter out very short or empty transcriptions
    if (type == "realtime") return;
    if (!text || text.trim().length < 3) return;

    const cleanedText = text.trim();

    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      if (lastTranscription) {
        const timeDiff = timestamp - lastTranscription.timestamp;

        // Within 3 seconds from same person - likely same utterance
        if (timeDiff < 3000) {
          // Exact duplicate
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip
          }

          // New text is extension of previous (progressive transcription)
          if (cleanedText.startsWith(lastTranscription.text)) {
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }

          // Previous text is prefix of new text (refined transcription)
          if (
            lastTranscription.text.startsWith(cleanedText) &&
            lastTranscription.text.length > cleanedText.length
          ) {
            return prev; // Keep the longer one
          }

          // Check for high similarity (likely correction/refinement)
          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.7) {
            // 70% similar - aggressive deduplication
            // Always replace with the latest version within 3 seconds
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }
        }

        // Between 3-8 seconds - check for very similar content
        if (timeDiff >= 3000 && timeDiff < 8000) {
          // Only merge if very similar or identical
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip exact duplicate
          }

          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.9) {
            // Very high similarity
            // Replace with longer version
            if (cleanedText.length > lastTranscription.text.length) {
              return prev.map((t, index) =>
                index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
              );
            }
            return prev; // Keep existing
          }
        }
      }

      const participant =
        mMeeting.participants?.get?.(participantId) ||
        mMeeting.participants[participantId];
      const profileImage =
        participant?.metaData?.profileImage ||
        participant?.value?.metaData?.profileImage ||
        null;

      // Create new transcription entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };

      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  };

  // Calculate text similarity (Levenshtein-based)
  function calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  // Simple edit distance calculation
  function getEditDistance(str1, str2) {
    const costs = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  }

  // Auto-scroll to bottom when new transcriptions arrive
  useEffect(() => {
    if (transcriptionListRef.current) {
      transcriptionListRef.current.scrollTop =
        transcriptionListRef.current.scrollHeight;
    }
  }, [transcriptions]);

  // Hide transcription panel when transcription is turned off
  useEffect(() => {
    if (!translationEnabled) {
      setIsTranscriptionPanelVisible(false);
    }
  }, [translationEnabled]);

  // Adjust panel position on window resize to keep it in bounds
  useEffect(() => {
    const handleResize = () => {
      setPanelPosition((prev) => {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });

      // Update panel size if it exceeds new window width
      setPanelSize((prev) => {
        const maxWidth = window.innerWidth;
        if (prev.width > maxWidth) {
          return {
            ...prev,
            width: Math.max(PANEL_MIN_WIDTH, maxWidth),
          };
        }
        return prev;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [panelSize.width, PANEL_FIXED_HEIGHT, isMobile, PANEL_MOBILE_HEIGHT]);

  // Handle panel dragging and resizing with limits
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        setPanelPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - dragOffset.y)),
        });
      }
      if (isResizing) {
        const panel = transcriptionPanelRef.current;
        if (panel) {
          const rect = panel.getBoundingClientRect();
          // Allow resizing up to full browser width
          const maxWidth = window.innerWidth;
          const panelHeight = isMobile
            ? PANEL_MOBILE_HEIGHT
            : PANEL_FIXED_HEIGHT;
          setPanelSize({
            width: Math.max(
              PANEL_MIN_WIDTH,
              Math.min(maxWidth, e.clientX - rect.left)
            ),
            height: panelHeight, // Keep height fixed
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    dragOffset,
    panelSize.width,
    PANEL_FIXED_HEIGHT,
    isMobile,
    PANEL_MOBILE_HEIGHT,
  ]);

  useEffect(() => {
    if (!isMobile || !isDragging) return;

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!e.touches[0]) return;

      const touch = e.touches[0];
      const panelWidth = panelSize.width;
      const panelHeight = PANEL_MOBILE_HEIGHT;
      const maxX = window.innerWidth - panelWidth;
      const maxY = window.innerHeight - panelHeight;

      const newX = Math.max(
        0,
        Math.min(maxX, touch.clientX - touchDragOffset.x)
      );
      const newY = Math.max(
        0,
        Math.min(maxY, touch.clientY - touchDragOffset.y)
      );

      setMobilePanelPosition({
        x: newX,
        y: window.innerHeight - newY - panelHeight,
      });
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [
    isDragging,
    isMobile,
    touchDragOffset,
    panelSize.width,
    PANEL_MOBILE_HEIGHT,
  ]);

  const handleDragStart = (e) => {
    // Allow dragging from anywhere on the panel, but not from resize handle
    if (e.target.closest(".cursor-ew-resize")) {
      return;
    }
    const panel = transcriptionPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    if (e.target.closest(".cursor-ew-resize")) {
      return;
    }

    const transcriptionList = transcriptionListRef.current;
    if (transcriptionList && transcriptionList.contains(e.target)) {
      const touch = e.touches[0];
      const listRect = transcriptionList.getBoundingClientRect();
      const touchY = touch.clientY - listRect.top;
      const isScrollable =
        transcriptionList.scrollHeight > transcriptionList.clientHeight;

      if (isScrollable) {
        const isNearTop = touchY < 60;
        const isNearBottom = touchY > listRect.height - 60;

        if (!isNearTop && !isNearBottom) {
          return;
        }
      }
    }

    const panel = transcriptionPanelRef.current;
    if (!panel) return;

    const touch = e.touches[0];
    const rect = panel.getBoundingClientRect();
    setTouchDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  // Whiteboard drag and resize handlers
  const handleWhiteboardDragStart = (e) => {
    const whiteboard = whiteboardRef.current;
    if (whiteboard && !isWhiteboardResizing) {
      const rect = whiteboard.getBoundingClientRect();
      setWhiteboardDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsWhiteboardDragging(true);
    }
  };

  const handleWhiteboardResizeStart = (e, handle) => {
    e.stopPropagation();
    setWhiteboardResizeHandle(handle);
    setIsWhiteboardResizing(true);
  };

  // Handle whiteboard dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isWhiteboardDragging) {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        setWhiteboardPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - whiteboardDragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - whiteboardDragOffset.y)),
        });
      }

      if (isWhiteboardResizing && whiteboardResizeHandle) {
        const whiteboard = whiteboardRef.current;
        if (whiteboard) {
          const rect = whiteboard.getBoundingClientRect();
          const minWidth = 400;
          const minHeight = 300;
          const maxWidth = window.innerWidth - rect.left;
          const maxHeight = window.innerHeight - rect.top;

          let newWidth = whiteboardSize.width;
          let newHeight = whiteboardSize.height;

          switch (whiteboardResizeHandle) {
            case "se": // southeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "sw": // southwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "ne": // northeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "nw": // northwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "e": // east
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              break;
            case "w": // west
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              break;
            case "s": // south
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "n": // north
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            default:
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
          }

          setWhiteboardSize({ width: newWidth, height: newHeight });

          // Adjust position for handles that resize from left or top
          if (["sw", "nw", "w", "n"].includes(whiteboardResizeHandle)) {
            const widthDiff = newWidth - whiteboardSize.width;
            const heightDiff = newHeight - whiteboardSize.height;

            let newX = whiteboardPosition.x;
            let newY = whiteboardPosition.y;

            if (["sw", "nw", "w"].includes(whiteboardResizeHandle)) {
              newX = whiteboardPosition.x - widthDiff;
            }
            if (["nw", "n"].includes(whiteboardResizeHandle)) {
              newY = whiteboardPosition.y - heightDiff;
            }

            setWhiteboardPosition({
              x: Math.max(0, newX),
              y: Math.max(0, newY),
            });
          }
        }
      }
    };

    const handleMouseUp = () => {
      setIsWhiteboardDragging(false);
      setIsWhiteboardResizing(false);
      setWhiteboardResizeHandle(null);
    };

    if (isWhiteboardDragging || isWhiteboardResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isWhiteboardDragging,
    isWhiteboardResizing,
    whiteboardDragOffset,
    whiteboardResizeHandle,
    whiteboardSize,
    whiteboardPosition,
  ]);

  // Keep whiteboard within bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      setWhiteboardPosition((prev) => {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [whiteboardSize]);

  // Passing callback functions to useTranscription hook
  const { startTranscription: startTranscriptionHook, stopTranscription } =
    useTranscription({
      onTranscriptionStateChanged,
      onTranscriptionText,
    });

  const handleStartTranscription = async () => {
    try {
      console.log("Starting transcription with config:", config);
      const result = startTranscriptionHook(config);

      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (error) {
      console.error("Error starting transcription:", error);
      toast.error("Failed to start transcription. Please try again.");
    }
  };

  useEffect(() => {
    if (["moderator", "co-host"].includes(meetingRole)) {
      getFolderElementList();
    }
  }, [meetingRole, meetingSettings]);

  const getFolderElementList = async () => {
    try {
      setIsElementRefreshing(true);
      const response = await getFolderElements(meetingSettings?.roomId);
      console.log("response", response.folders);
      if (response.folders) {
        setFolderElementList(response.folders);
      }
      setIsElementRefreshing(false);
    } catch (error) {
      setIsElementRefreshing(false);
    }
  };

  return (
    <div className="fixed inset-0">
      {["moderator", "co-host"].includes(meetingRole) &&
        approvalSoundEnabled && (
          <audio preload="auto" ref={approvalSoundRef} src={test_sound} />
        )}
      <div ref={containerRef} className="h-full flex flex-col bg-gray-800">
        {showPollResults && (
          <PollResults onClose={() => setShowPollResults(false)} />
        )}

        {showElementSection && (
          <div
            className={`fixed z-50 border border-gray-600 overflow-hidden 
              left-0 w-full top-16 ${isMobileLike
                ? "h-[calc(100vh-4rem-5rem)]"
                : "h-[calc(100vh-4rem)]"
              } m-0 bg-gray-800
              md:left-0 md:w-[30%] md:top-[60px] md:h-[calc(100vh-60px-2rem)] md:rounded-lg`}
          >
            <div className="relative h-full">
              {/* Close moved inside header in ElementSection on mobile */}
              <div className="h-full overflow-y-auto">
                <ElementSection
                  getFolderElementList={getFolderElementList}
                  isRefreshing={isElementRefreshing}
                />
              </div>
            </div>
          </div>
        )}
        {activeElement && activeElement.type === "cta" && (
          <CtaSection sideBarContainerWidth={sideBarContainerWidth} />
        )}
        {activeElement && activeElement.type === "video" && (
          <div
            className="fixed left-0 right-0 bottom-0 top-16 z-50"
            style={{ minHeight: "100%" }}
          >
            <VideoSection
              fileUrl={JSON.parse(activeElement.settings).fileUrl}
              onClosePanel={() => {
                if (meetingRole === "moderator") {
                  publishCloseElementSection(
                    JSON.stringify({
                      set: true,
                    })
                  );
                  setShowElementSection(true);
                } else {
                  setActiveElement(null);
                }
              }}
              sideBarContainerWidth={sideBarContainerWidth}
            />
          </div>
        )}
        {activeElement && activeElement.type === "website" && (
          <WebsiteSection
            settings={JSON.parse(activeElement.settings)}
            onClose={() => {
              if (meetingRole === "moderator") {
                publishCloseElementSection(
                  JSON.stringify({
                    set: true,
                  })
                );
                setShowElementSection(true);
              } else {
                setActiveElement(null);
              }
            }}
            sideBarContainerWidth={sideBarContainerWidth}
          />
          // <WebViewer htmlUrl="https://www.facebook.com" />
        )}
        {activeElement && activeElement.type == "document" && (
          <div
            className={`fixed z-50 border border-gray-600 overflow-hidden left-0 w-full top-16 ${
              isMobileLike ? "h-[calc(100vh-4rem-5rem)]" : "h-[calc(100vh-4rem)]"
            } m-0 bg-gray-800`}
          >
            <DocumentViewer
              fileUrl={JSON.parse(activeElement.settings).fileUrl}
              onClosePanel={() => {
                if (meetingRole === "moderator") {
                  publishCloseElementSection(
                    JSON.stringify({
                      set: true,
                    })
                  );
                  setShowElementSection(true);
                } else {
                  setActiveElement(null);
                }
              }}
              sideBarContainerWidth={sideBarContainerWidth}
            />
          </div>
        )}
        {typeof localParticipantAllowedJoin === "boolean" &&
          showJoinScreen == false ? (
          localParticipantAllowedJoin ? (
            <>
              {/* <BottomBar
                                bottomBarHeight={bottomBarHeight}
                                setIsMeetingLeft={setIsMeetingLeft}
                            /> */}
              <TopBar
                setIsMeetingLeft={setIsMeetingLeft}
                setCustomVideoStream={setCustomVideoStream}
                micOn={micOn}
                setMicOn={setMicOn}
                webcamOn={webcamOn}
                setWebcamOn={setWebcamOn}
                customAudioStream={customAudioStream}
                setCustomAudioStream={setCustomAudioStream}
                customVideoStream={customVideoStream}
                startTranscription={handleStartTranscription}
                stopTranscription={stopTranscription}
                startTranslation={handleStartTranslation}
                stopTranslation={handleStopTranslation}
                changeTranslationLanguage={changeTranslationLanguage}
                setShowPollResults={setShowPollResults}
                showPollResults={showPollResults}
                publishMeetingSettings={publishMeetingSettings}
              />
              <div
                className={`flex flex-1 flex-row bg-gray-800 ${showElementSection ? "ml-[22rem]" : ""
                  } transition-all duration-300 ${isMobileLandscape
                    ? `pr-20 ${effectiveIsPresenting && !isBottomParticipantCollapsed
                      ? "pb-24"
                      : ""
                    }`
                    : isMobileLike
                      ? "pb-24"
                      : ""
                  }`}
                style={{
                  ...(isMobileLandscape && {
                    paddingRight: `${(isMobilePanelOpen ? mobilePanelWidth : 0) + 80
                      }px`,
                  }),
                  ...(meetingSettings?.meetingBackgroundImage && {
                    backgroundImage: `url(${meetingSettings.meetingBackgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }),
                  ...(meetingSettings?.meetingBackgroundGradient && {
                    backgroundImage: `linear-gradient(${meetingSettings.meetingBackgroundGradient.gradientDirection}, ${meetingSettings.meetingBackgroundGradient.gradientColor1}, ${meetingSettings.meetingBackgroundGradient.gradientColor2})`,
                  }),
                  ...(!meetingSettings?.meetingBackgroundImage &&
                    !meetingSettings?.meetingBackgroundGradient && {
                    backgroundColor:
                      meetingSettings?.meetingBackgroundSolid || "#050a0e",
                  }),
                }}
              >
                {effectiveIsPresenting && (
                  <div
                    className={`flex flex-1 ${isPresenting ? "flex-col" : ""} ${isMobileLandscape ? "overflow-hidden" : ""
                      }`}
                    style={{
                      ...(isMobileLandscape && {
                        maxHeight: "100%",
                        height: "100%",
                      }),
                    }}
                  >
                    {participantsData.map((participantId) => (
                      <ParticipantMicStream
                        key={participantId}
                        participantId={participantId}
                      />
                    ))}

                    <PresenterView
                      height={
                        isMobileLandscape
                          ? Math.max(
                            0,
                            containerHeight -
                            bottomBarHeight -
                            (isBottomParticipantCollapsed ? 0 : 120)
                          )
                          : isMobileLike
                            ? Math.max(0, containerHeight - 96)
                            : Math.max(
                              0,
                              containerHeight -
                              bottomBarHeight -
                              (isBottomParticipantCollapsed ? 0 : 220)
                            )
                      }
                    // height={540}
                    />

                    {!isMobile && (
                      <>
                        {!isBottomParticipantCollapsed && (
                          <div
                            className={`relative ${isMobileLandscape ? "pb-2" : ""
                              }`}
                            style={{
                              ...(isMobileLandscape && {
                                minHeight: "120px",
                                maxHeight: "120px",
                                overflow: "visible",
                              }),
                            }}
                          >
                            <MemorizedParticipantView
                              isPresenting={isPresenting}
                            />
                            {/* Collapse button */}
                            <button
                              onClick={() =>
                                setIsBottomParticipantCollapsed(true)
                              }
                              className="absolute top-2 left-2 z-50 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-2 shadow-lg transition-all duration-200 flex items-center justify-center collapse-button-animate"
                              title="Collapse participants"
                              style={{ width: "32px", height: "32px" }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-4 h-4 arrow-pulse-animate"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() =>
                                setIsBottomParticipantCollapsed(true)
                              }
                              className="absolute top-2 right-2 z-50 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-2 shadow-lg transition-all duration-200 flex items-center justify-center collapse-button-animate"
                              title="Collapse participants"
                              style={{ width: "32px", height: "32px" }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-4 h-4 arrow-pulse-animate"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                        {isBottomParticipantCollapsed && (
                          <>
                            {/* Expand button - fixed at bottom when collapsed */}
                            <button
                              onClick={() =>
                                setIsBottomParticipantCollapsed(false)
                              }
                              className={`fixed z-50 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-2 shadow-lg transition-all duration-200 flex items-center justify-center expand-button-animate ${isMobileLandscape ? "bottom-4" : "bottom-20"
                                }`}
                              title="Expand participants"
                              style={{
                                width: "36px",
                                height: "36px",
                                right:
                                  sideBarMode && !isMobileLandscape
                                    ? `${sideBarContainerWidth + 16}px`
                                    : isMobileLandscape
                                      ? `${(isMobilePanelOpen
                                        ? mobilePanelWidth
                                        : 0) +
                                      80 +
                                      16
                                      }px`
                                      : "16px",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-5 h-5 arrow-pulse-animate"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                                />
                              </svg>
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {selectedLayout === "gallery" && !isPresenting && (
                  <div
                    className={`flex flex-1 min-h-0 ${isPresenting ? "flex-col" : ""
                      }`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <MemorizedParticipantView isPresenting={isPresenting} />
                    )}
                  </div>
                )}

                {selectedLayout === "focus" && !isPresenting && (
                  <div
                    className={`flex flex-1 min-h-0 ${isPresenting ? "flex-col" : ""
                      }`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <FocusParticipantView isPresenting={isPresenting} />
                    )}
                  </div>
                )}

                {selectedLayout === "speaker-top" && !isPresenting && (
                  <div className={`flex flex-1 flex-col min-h-0 overflow-hidden`}>
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <>
                        <div className="flex-[2] min-h-0 overflow-hidden">
                          <SpeakerParticipantView
                            height={containerHeight - bottomBarHeight - 220}
                            isPresenting={isPresenting}
                          />
                        </div>
                        <div className="flex-shrink-0 w-full overflow-x-hidden overflow-y-auto max-h-[30vh] md:max-h-none px-2 md:px-0">
                          <MemorizedParticipantView
                            isPresenting={true}
                            layout="speaker-top"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {selectedLayout === "speaker-side" && !isPresenting && (
                  <div
                    className={`flex flex-1 min-h-0 p-3 gap-2 md:gap-3 overflow-hidden ${isPresenting ? "flex-col" : "flex-row"
                      }`}
                  >
                    {isPresenting ? (
                      <PresenterView
                        height={containerHeight - bottomBarHeight - 220}
                      // height={540}
                      />
                    ) : null}
                    {isPresenting && isMobile ? (
                      participantsData.map((participantId) => (
                        <ParticipantMicStream
                          key={participantId}
                          participantId={participantId}
                        />
                      ))
                    ) : (
                      <>
                        <div className="flex-[3] min-h-0 min-w-0 overflow-hidden flex items-center justify-center">
                          <SpeakerSideParticipantView
                            isPresenting={isPresenting}
                          />
                        </div>
                        <div className="flex-shrink-0 w-24 sm:w-32 md:w-60 lg:w-64 2xl:w-80 max-w-[40%] overflow-hidden md:px-0 flex flex-col">
                          <div
                            className={`w-full flex flex-col ${isMobileLike ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden h-full"}`}
                            style={isMobileLike && containerHeight ? {
                              height: `${containerHeight - bottomBarHeight - 96}px`,
                              maxHeight: `${containerHeight - bottomBarHeight - 96}px`
                            } : {}}
                          >
                            <MemorizedParticipantView
                              isPresenting={true}
                              isFlexCol={true}
                              layout="speaker-side"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <SidebarConatiner
                  height={containerHeight - bottomBarHeight}
                  sideBarContainerWidth={sideBarContainerWidth}
                />
              </div>
              {whiteboardUrl && (
                <div
                  ref={whiteboardRef}
                  className={`fixed z-40 bg-white shadow-2xl overflow-hidden border-2 ${isMobile ? "left-4 right-4 top-4" : ""
                    }`}
                  style={{
                    ...(isMobile
                      ? {
                        bottom: `${bottomBarHeight + 16}px`,
                        cursor: "default",
                        borderColor: "#928D9B",
                        borderRadius: "10px",
                        boxShadow:
                          "0 10px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(146, 141, 155, 0.2)",
                      }
                      : {
                        left: `${whiteboardPosition.x}px`,
                        top: `${whiteboardPosition.y}px`,
                        width: `${whiteboardSize.width}px`,
                        height: `${whiteboardSize.height}px`,
                        cursor: isWhiteboardDragging ? "grabbing" : "default",
                        borderColor: "#928D9B",
                        borderRadius: "10px",
                        boxShadow:
                          "0 10px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(146, 141, 155, 0.2)",
                      }),
                  }}
                >
                  {/* Draggable Header */}
                  <div
                    className="absolute top-0 left-0 right-0 h-10 flex items-center justify-between px-3 z-20"
                    onMouseDown={
                      !isMobile ? handleWhiteboardDragStart : undefined
                    }
                    style={{
                      ...(!isMobile
                        ? { cursor: "grab" }
                        : { cursor: "default" }),
                      userSelect: "none",
                      backgroundColor: "#030325",
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      <span className="text-white text-sm font-medium">
                        Whiteboard
                      </span>
                    </div>
                    {meetingRole === "moderator" && (
                      <button
                        onClick={() => stopWhiteboard()}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors"
                        title="Close Whiteboard"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Whiteboard Content */}
                  <div className="w-full h-full pt-10">
                    <iframe
                      src={whiteboardUrl}
                      className={`w-full h-full border-0 ${meetingRole === "moderator"
                        ? "pointer-events-auto"
                        : "pointer-events-none"
                        }`}
                      title="Whiteboard"
                    />
                  </div>

                  {/* Resize Handles */}
                  {meetingRole === "moderator" && !isMobile && (
                    <>
                      {/* Corner handles */}
                      <div
                        className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-nw-resize opacity-70 hover:opacity-100 transition-opacity"
                        style={{
                          top: "-2px",
                          left: "-2px",
                          borderRadius: "50% 0 0 0",
                        }}
                        onMouseDown={(e) =>
                          handleWhiteboardResizeStart(e, "nw")
                        }
                        title="Resize from top-left"
                      />
                      <div
                        className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-ne-resize opacity-70 hover:opacity-100 transition-opacity"
                        style={{
                          top: "-2px",
                          right: "-2px",
                          borderRadius: "0 50% 0 0",
                        }}
                        onMouseDown={(e) =>
                          handleWhiteboardResizeStart(e, "ne")
                        }
                        title="Resize from top-right"
                      />
                      <div
                        className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-sw-resize opacity-70 hover:opacity-100 transition-opacity"
                        style={{
                          bottom: "-2px",
                          left: "-2px",
                          borderRadius: "0 0 0 50%",
                        }}
                        onMouseDown={(e) =>
                          handleWhiteboardResizeStart(e, "sw")
                        }
                        title="Resize from bottom-left"
                      />
                      <div
                        className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-se-resize opacity-70 hover:opacity-100 transition-opacity"
                        style={{
                          bottom: "-2px",
                          right: "-2px",
                          borderRadius: "0 0 50% 0",
                        }}
                        onMouseDown={(e) =>
                          handleWhiteboardResizeStart(e, "se")
                        }
                        title="Resize from bottom-right"
                      />

                      {/* Edge handles */}
                      <div
                        className="absolute w-1 h-full bg-blue-400 hover:bg-blue-500 cursor-ew-resize opacity-50 hover:opacity-80 transition-opacity"
                        style={{ left: "-2px", top: "0" }}
                        onMouseDown={(e) => handleWhiteboardResizeStart(e, "w")}
                        title="Resize width from left"
                      />
                      <div
                        className="absolute w-1 h-full bg-blue-400 hover:bg-blue-500 cursor-ew-resize opacity-50 hover:opacity-80 transition-opacity"
                        style={{ right: "-2px", top: "0" }}
                        onMouseDown={(e) => handleWhiteboardResizeStart(e, "e")}
                        title="Resize width from right"
                      />
                      <div
                        className="absolute w-full h-1 bg-blue-400 hover:bg-blue-500 cursor-ns-resize opacity-50 hover:opacity-80 transition-opacity"
                        style={{ top: "-2px", left: "0" }}
                        onMouseDown={(e) => handleWhiteboardResizeStart(e, "n")}
                        title="Resize height from top"
                      />
                      <div
                        className="absolute w-full h-1 bg-blue-400 hover:bg-blue-500 cursor-ns-resize opacity-50 hover:opacity-80 transition-opacity"
                        style={{ bottom: "-2px", left: "0" }}
                        onMouseDown={(e) => handleWhiteboardResizeStart(e, "s")}
                        title="Resize height from bottom"
                      />
                    </>
                  )}
                </div>
              )}

              {/* Transcription Panel - Transparent Style */}
              {(translationEnabled || transcriptionEnabled) &&
                (isMobile ? true : transcriptions) && (
                  <div
                    ref={transcriptionPanelRef}
                    className="fixed z-50 flex flex-col"
                    style={{
                      ...(isMobile
                        ? {
                          left: `${mobilePanelPosition.x}px`,
                          bottom: `calc(${Math.max(
                            mobilePanelPosition.y,
                            bottomBarHeight + 26
                          )}px + env(safe-area-inset-bottom))`,
                          top: "auto",
                          transform: "none",
                          width: `${Math.min(
                            panelSize.width,
                            window.innerWidth - 32
                          )}px`,
                        }
                        : {
                          left: `${panelPosition.x}px`,
                          top: `${panelPosition.y}px`,
                          width: `${panelSize.width}px`,
                        }),
                      // height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                      //   }px`,
                      // cursor: isDragging ? "grabbing" : "grab",
                      // boxShadow: "0 4px 15px rgba(0, 0, 0, 0.5)",
                      // borderRadius: "8px",
                      // backgroundColor: "rgba(0, 0, 0, 0.35)",
                      // backdropFilter: "blur(10px)",
                      // border: "1px solid rgba(255, 255, 255, 0.2)",
                      // userSelect: "none",
                      // touchAction: isMobile ? "pan-x pan-y" : "none",
                    }}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleTouchStart}
                  >
                    {/* <div
                      ref={transcriptionListRef}
                      className="flex-1 overflow-y-auto"
                      style={{
                        overflowY: "auto",
                        backgroundColor: "#242424",
                        borderRadius: "8px",
                        height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                          }px`,
                        ...(isMobile && {
                          touchAction: "pan-y",
                        }),
                      }}
                    > */}
                    <div
                      ref={transcriptionListRef}
                      className="flex-1 overflow-y-auto flex items-center justify-center"
                      style={{
                        overflowY: "auto",
                        backgroundColor: "transparent",
                        borderRadius: "8px",
                        height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                          }px`,
                        ...(isMobile && {
                          touchAction: "pan-y",
                        }),
                      }}
                    >
                      {!transcriptions ? (
                        <div className="w-full flex items-center justify-center px-4 py-2">
                          <div
                            style={{
                              backgroundColor: "rgba(0, 0, 0, 0.75)",
                              padding: "10px 16px",
                              borderRadius: "8px",
                              maxWidth: "90%",
                              color: "#fff",
                              fontSize: "14px",
                              textAlign: "center",
                            }}
                          >
                            Captions will appear here.
                          </div>
                        </div>
                      ) : (
                        <div className="w-full flex items-center justify-center px-4 py-2">
                          {(() => {
                            // Check if selected language is Arabic (RTL)
                            const isRTL = selectedLanguage?.startsWith("ar");
                            const textDirection = isRTL ? "rtl" : "ltr";
                            const textAlign = isRTL ? "right" : "left";

                            return (
                              <div
                                style={{
                                  backgroundColor: "rgba(0, 0, 0, 0.75)",
                                  padding: "12px 20px",
                                  borderRadius: "8px",
                                  maxWidth: "90%",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  direction: textDirection,
                                }}
                              >
                                {/* Profile Image or Initial */}
                                {(() => {
                                  const participant =
                                    mMeeting?.participants?.get?.(
                                      transcriptions.participantId
                                    ) ||
                                    mMeeting?.participants?.[
                                    transcriptions.participantId
                                    ];
                                  const profileImage =
                                    transcriptions.profileImage ||
                                    participant?.metaData?.profileImage ||
                                    participant?.value?.metaData
                                      ?.profileImage ||
                                    null;
                                  const participantName =
                                    transcriptions.participantName || "User";
                                  const firstChar =
                                    participantName?.charAt(0)?.toUpperCase() ||
                                    "?";

                                  // Generate a color based on participant ID for consistency
                                  const colors = [
                                    "rgba(45, 140, 255, 0.8)",
                                    "rgba(255, 140, 45, 0.8)",
                                    "rgba(140, 45, 255, 0.8)",
                                    "rgba(45, 255, 140, 0.8)",
                                    "rgba(255, 45, 140, 0.8)",
                                  ];
                                  const colorIndex =
                                    transcriptions.participantId?.charCodeAt(
                                      0
                                    ) % colors.length || 0;
                                  const bgColor = colors[colorIndex];

                                  return (
                                    <div
                                      style={{
                                        flexShrink: 0,
                                        width: isMobile ? "36px" : "40px",
                                        height: isMobile ? "36px" : "40px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: profileImage
                                          ? "transparent"
                                          : bgColor,
                                      }}
                                    >
                                      {profileImage ? (
                                        <img
                                          src={profileImage}
                                          alt={participantName}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <span
                                          style={{
                                            color: "#FFFFFF",
                                            fontSize: isMobile
                                              ? "16px"
                                              : "18px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {firstChar}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                <p
                                  className="text-white break-words"
                                  style={{
                                    fontSize: isMobile ? "18px" : "20px",
                                    lineHeight: "1.6",
                                    fontFamily: "Arial, Helvetica, sans-serif",
                                    fontWeight: "500",
                                    textAlign: textAlign,
                                    flex: 1,
                                    minWidth: 0, // Ensures text doesn't overflow
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {transcriptions.text}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Right Edge Resize Handle - Visual Indicator */}
                    <div
                      className="absolute right-0 w-3 cursor-ew-resize group"
                      onMouseDown={handleResizeStart}
                      title="Drag to resize width"
                      style={{
                        top: "0px",
                        bottom: "0px",
                        borderBottomRightRadius: "8px",
                        borderTopRightRadius: "8px",
                      }}
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-white/20" />
                    </div>
                  </div>
                )}
            </>
          ) : (
            <></>
          )
        ) : (
          <>
            {showJoinScreen && <WaitingToJoinScreenNew />}
            {showWaitingApproval && <WaitingForApproval />}
          </>
        )}
      </div>

      {/* Poll Voting Modal */}
      <PollVotingModal
        poll={activePoll}
        isOpen={showPollModal}
        onClose={() => {
          setShowPollModal(false);
          setActivePoll(null);
        }}
        onVoteSubmitted={() => {
          // Optionally handle vote submission callback
          console.log("Vote submitted for poll:", activePoll?.id);
        }}
      />

      {/* Participant Permission Modals */}
      <ParticipantPermissionModal
        open={showMicPermissionModal}
        onAccept={handleAcceptMicPermission}
        onDecline={handleDeclineMicPermission}
        permissionType="mic"
      />
      <ParticipantPermissionModal
        open={showVideoPermissionModal}
        onAccept={handleAcceptVideoPermission}
        onDecline={handleDeclineVideoPermission}
        permissionType="video"
      />
    </div>
  );
}
